/*
 * PlateEngine — shared rendering + production logic for the license-plate
 * headcover customizer. Used by both the builder (index.html/app.js) and the
 * storefront product widget (product.html/product.js) so they render identically.
 *
 * No DOM dependencies: every function takes explicit parameters.
 */
(function (global) {
  "use strict";

  const NAME_SIZE_RATIO = 0.159; // name size as a fraction of cover height
  const ART_W = 540;
  const ART_H = 1134;
  const STITCH_NUDGE = 0.1; // stitch lightness offset from the letter color

  // ---- color helpers ----
  function toRgb(hex) {
    const h = hex.replace("#", "");
    const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
  }

  // Shift HSL lightness by dL (hue + saturation preserved), so a lightened navy
  // still reads as navy rather than drifting toward gray.
  function shiftLightness(hex, dL) {
    let [r, g, b] = toRgb(hex).map((v) => v / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    l = Math.max(0, Math.min(1, l + dL));
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    let R, G, B;
    if (s === 0) {
      R = G = B = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      R = hue2rgb(p, q, h + 1 / 3);
      G = hue2rgb(p, q, h);
      B = hue2rgb(p, q, h - 1 / 3);
    }
    return `rgb(${Math.round(R * 255)}, ${Math.round(G * 255)}, ${Math.round(B * 255)})`;
  }

  // Stitch thread: essentially the letter color, nudged a hair in lightness —
  // darker on light letters, lighter on dark ones.
  function stitchShade(hex) {
    const [r, g, b] = toRgb(hex);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const dir = lum > 0.5 ? -1 : 1;
    return shiftLightness(hex, dir * STITCH_NUDGE);
  }

  // Distance from the start of the string to the center of its MIDDLE slot.
  // For odd-length text that slot is the middle character (a space or a letter);
  // for even-length text it's the seam between the two middle characters. Used
  // to anchor that slot at the plate's center so names don't drift vertically
  // with letter/kerning widths (JILL NUNN lines up with YOUR NAME). Requires the
  // font already set on c.
  function centerCharOffset(c, t) {
    const n = t.length;
    if (!n) return 0;
    const ci = (n - 1) / 2; // fractional center index
    const k = Math.floor(ci);
    const left = c.measureText(t.slice(0, k)).width;
    const right = c.measureText(t.slice(0, k + 1)).width;
    return ci === k ? (left + right) / 2 : right;
  }

  // ---- draw the stitched name onto a transparent W x H context ----
  // opts: { text, color, geom: {cx, cy} }
  function drawName(ctx, W, H, opts) {
    const text = (opts.text || "").toUpperCase();
    if (!text) return;

    const fs = H * NAME_SIZE_RATIO;
    const g = opts.geom || { cx: 45, cy: 50 };
    const color = opts.color;
    const stitch = stitchShade(color);

    const tx = (W * g.cx) / 100;
    const ty = (H * g.cy) / 100;

    const setup = (c) => {
      c.font = `${fs}px "LicensePlate"`;
      c.textAlign = "center";
      c.textBaseline = "alphabetic";
      if ("letterSpacing" in c) c.letterSpacing = `${0.02 * fs}px`;
      c.lineJoin = "round";
      c.lineCap = "butt";
    };

    // letter fill + soft shadow into the leather
    ctx.save();
    ctx.translate(tx, ty);
    ctx.rotate(Math.PI / 2);
    setup(ctx);
    ctx.textAlign = "left";
    const m = ctx.measureText(text);
    const dy = (m.fontBoundingBoxAscent - m.fontBoundingBoxDescent) / 2;
    // Anchor the string's middle slot (space or letter) at the center point so
    // names sit at a consistent height regardless of letter/kerning widths.
    const xoff = -centerCharOffset(ctx, text);
    ctx.shadowColor = "rgba(0, 0, 0, 0.38)";
    ctx.shadowBlur = 0.05 * fs;
    ctx.shadowOffsetX = 0.01 * fs;
    ctx.shadowOffsetY = 0.015 * fs;
    ctx.fillStyle = color;
    ctx.fillText(text, xoff, dy);
    ctx.restore();

    // dashed satin stitch, masked to inside the letters and inset from the edge
    const inset = 0.022 * fs;
    const stitchW = 0.013 * fs;
    const oc = document.createElement("canvas");
    oc.width = W;
    oc.height = H;
    const o = oc.getContext("2d");
    o.translate(tx, ty);
    o.rotate(Math.PI / 2);
    setup(o);
    o.textAlign = "left";
    o.strokeStyle = stitch;
    o.lineWidth = 2 * (inset + stitchW);
    o.setLineDash([0.03 * fs, 0.026 * fs]);
    o.strokeText(text, xoff, dy);
    o.setLineDash([]);
    o.globalCompositeOperation = "destination-in";
    o.fillText(text, xoff, dy);
    o.globalCompositeOperation = "destination-out";
    o.lineWidth = 2 * inset;
    o.strokeText(text, xoff, dy);
    o.globalCompositeOperation = "source-over";

    ctx.drawImage(oc, 0, 0);
  }

  // Render a full transparent cover PNG canvas: plate artwork + stitched name.
  // opts: { text, color, width? }
  async function renderPlate(plate, opts) {
    const W = opts.width || 800;
    const H = Math.round(W * (ART_H / ART_W));
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.src = plate.svg;
    await img.decode();
    ctx.drawImage(img, 0, 0, W, H);

    try { await document.fonts.load(`${H * NAME_SIZE_RATIO}px "LicensePlate"`); } catch (e) {}
    drawName(ctx, W, H, { text: opts.text, color: opts.color, geom: plate.geom });
    return canvas;
  }

  // ---- production code ----
  // Spaces -> dashes; padded (centered) with dashes to the shop template size.
  // Odd character counts use the 9-char template, even counts the 8-char template.
  function productionCode(rawText) {
    const text = (rawText || "").toUpperCase();
    const count = text.length; // includes spaces
    const odd = count % 2 === 1;
    const size = odd ? 9 : 8;
    let s = text.replace(/ /g, "-");
    const pad = Math.max(0, size - s.length);
    const left = Math.floor(pad / 2);
    s = "-".repeat(left) + s + "-".repeat(pad - left);
    return { code: s, template: odd ? "ODD" : "EVEN", size, count };
  }

  // Production sheet: a cover canvas on white with the code + template below.
  function renderProductionSheet(plateCanvas, rawText) {
    const W = plateCanvas.width;
    const coverH = plateCanvas.height;
    const footer = 300;
    const c = document.createElement("canvas");
    c.width = W;
    c.height = coverH + footer;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(plateCanvas, 0, 0);

    ctx.strokeStyle = "#dddddd";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(60, coverH + 26);
    ctx.lineTo(W - 60, coverH + 26);
    ctx.stroke();

    const info = productionCode(rawText);
    ctx.textAlign = "center";
    ctx.fillStyle = "#111111";
    ctx.font = '700 104px "Courier New", monospace';
    ctx.fillText(info.code, W / 2, coverH + 168);
    ctx.fillStyle = "#555555";
    ctx.font = '600 34px Inter, system-ui, sans-serif';
    ctx.fillText(`${info.template} TEMPLATE`, W / 2, coverH + 236);
    return c;
  }

  function safeName(s) {
    return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }

  function downloadCanvas(canvas, filename) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        resolve();
      }, "image/png");
    });
  }

  global.PlateEngine = {
    NAME_SIZE_RATIO,
    ART_W,
    ART_H,
    toRgb,
    shiftLightness,
    stitchShade,
    drawName,
    renderPlate,
    productionCode,
    renderProductionSheet,
    safeName,
    downloadCanvas,
  };
})(window);
